<script setup lang="ts">
/**
 * Ce qu'est devenue la dernière écriture sur une cible, dit sur sa ligne,
 * jamais en bannière.
 *
 * Ce que l'utilisateur doit vivre : pendant l'envoi, rien n'a encore changé
 * et l'écran le dit ; un succès se dit une fois la réponse reçue ; un refus
 * se dit par sa phrase ; quand on ne sait pas si l'écriture est passée,
 * l'écran le dit, relit, et ne propose de recommencer qu'après une relecture
 * réussie. Une relecture échouée propose « Relire » (une lecture, jamais
 * l'écriture).
 */
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { useActionsStore } from "@modules/alarmes-v2/stores/actions.store"
import { phraseDuRefus } from "@modules/alarmes-v2/utils/ecritures"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n()

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(
    defineProps<{
        /** La cible (`surveillance:<id>`, `alarme:<id>`…). */
        cible: string
        /** Ce que dit un succès, calculé par l'écran depuis la réponse. */
        texteFait?: string
        /** Ce que dit une incertitude, si ce n'est pas une surveillance. */
        texteIncertain?: string
        /** La relecture de l'écran, pour « Relire ». */
        relire: () => Promise<boolean>
    }>(),
    { texteFait: "", texteIncertain: "" }
)

const actions = useActionsStore()

// ================================
// COMPUTED
// ================================
const getEtat = computed(() => actions.etat(props.cible))

/** La ligne à dire, son ton, et si « Relire » est proposé. */
const getAnnonce = computed(
    (): { texte: string; ton: string; relire: boolean } | null => {
        const e = getEtat.value
        if (!e) return null
        if (e.statut === "envoi")
            return {
                texte: t("alarmesV2.actions.envoi"),
                ton: "neutre",
                relire: false,
            }
        if (e.statut === "incertain") {
            if (e.relecture === "faite")
                return {
                    texte: props.texteIncertain
                        ? props.texteIncertain
                        : t("alarmesV2.actions.incertainRelu"),
                    ton: "warn",
                    relire: false,
                }
            if (e.relecture === "echouee")
                return {
                    texte: props.texteIncertain
                        ? props.texteIncertain
                        : t("alarmesV2.actions.incertainNonRelu"),
                    ton: "warn",
                    relire: true,
                }
            return {
                texte: props.texteIncertain
                    ? props.texteIncertain
                    : t("alarmesV2.actions.incertain"),
                ton: "warn",
                relire: false,
            }
        }
        const lignes: string[] = []
        if (e.statut === "refus" && e.erreur) {
            const p = phraseDuRefus(e.erreur)
            lignes.push(p.texte ?? t(p.cle as string))
        } else if (e.statut === "fait" && props.texteFait) {
            lignes.push(props.texteFait)
        }
        if (e.relecture === "en_cours")
            lignes.push(t("alarmesV2.actions.relecture"))
        if (e.relecture === "echouee")
            lignes.push(t("alarmesV2.actions.nonRelu"))
        if (lignes.length === 0) return null
        return {
            texte: lignes.join(" "),
            ton: e.statut === "refus" ? "danger" : "ok",
            relire: e.relecture === "echouee",
        }
    }
)

/** Les classes d'un ton. */
const TONS: Record<string, string> = {
    neutre: "text-surface-700",
    ok: "text-emerald-800",
    warn: "text-amber-800",
    danger: "text-red-800",
}

// ================================
// HANDLERS
// ================================
function onRelire(): void {
    void actions.relireLaCible(props.cible, props.relire)
}
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL -->
    <!-- ================================ -->
    <p
        role="status"
        aria-live="polite"
        class="text-sm"
        :class="getAnnonce ? TONS[getAnnonce.ton] : ''"
        data-test="annonce"
    >
        <template v-if="getAnnonce">
            {{ getAnnonce.texte }}
            <button
                v-if="getAnnonce.relire"
                type="button"
                class="ml-1 font-medium text-primary-700 underline"
                data-test="annonce-relire"
                @click="onRelire"
            >
                {{ t("alarmesV2.refus.relire") }}
            </button>
        </template>
    </p>
</template>
